import React from 'react';
import DashboardTopBar from '../DashboardTopBar';
import { UI_THEME } from '../../ui/theme';

interface Props {
  sidebar: React.ReactNode;
  sidebarHidden: boolean;
  setSidebarHidden: React.Dispatch<React.SetStateAction<boolean>>;
  sessionExpiring: boolean;
  onSessionLogin: () => void;
  children: React.ReactNode;
}

const DashboardShell: React.FC<Props> = ({
  sidebar,
  sidebarHidden,
  setSidebarHidden,
  sessionExpiring,
  onSessionLogin,
  children,
}) => (
  <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-950 dark:text-slate-100">
    <div className="flex">
      <aside
        className={`${UI_THEME === 'minimal' ? 'dashboard-sidebar border-sidebar-line bg-sidebar' : 'border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900'} fixed left-0 top-0 z-40 flex h-screen w-72 shrink-0 flex-col border-r transition-transform duration-200 ${
          sidebarHidden ? '-translate-x-full' : 'translate-x-0'
        }`}
        aria-hidden={sidebarHidden}
      >
        <button
          type="button"
          onClick={() => setSidebarHidden((value) => !value)}
          className="absolute -right-3 top-20 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          aria-label="Ocultar menu lateral"
          title="Ocultar menu lateral"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M12.78 4.22a.75.75 0 0 1 0 1.06L8.06 10l4.72 4.72a.75.75 0 1 1-1.06 1.06l-5.25-5.25a.75.75 0 0 1 0-1.06l5.25-5.25a.75.75 0 0 1 1.06 0Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {sidebar}
      </aside>

      {!sidebarHidden && (
        <button
          type="button"
          onClick={() => setSidebarHidden(true)}
          className="fixed inset-0 z-30 bg-slate-950/35 md:hidden"
          aria-label="Fechar menu lateral"
        />
      )}

      {sidebarHidden && (
        <button
          type="button"
          onClick={() => setSidebarHidden(false)}
          className="fixed bottom-4 left-4 z-50 inline-flex h-10 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-300 md:bottom-auto md:top-20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
          aria-label="Mostrar menu lateral"
          title="Mostrar menu lateral"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M7.22 15.78a.75.75 0 0 1 0-1.06L11.94 10 7.22 5.28a.75.75 0 0 1 1.06-1.06l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 0 1-1.06 0Z"
              clipRule="evenodd"
            />
          </svg>
          <span>Menu</span>
        </button>
      )}

      <main className={`ml-0 min-w-0 flex-1 transition-[margin] duration-200 ${sidebarHidden ? '' : 'md:ml-72'}`}>
        <DashboardTopBar />
        {sessionExpiring && (
          <div className="sticky top-0 z-30 flex items-center justify-between gap-3 bg-amber-500 px-4 py-2.5 text-sm font-medium text-white shadow">
            <span>Sua sessão expira em breve. Salve seu trabalho e faça login novamente para continuar.</span>
            <button
              type="button"
              onClick={onSessionLogin}
              className="shrink-0 rounded-lg border border-white/40 px-3 py-1 text-xs font-semibold hover:bg-amber-600"
            >
              Fazer login
            </button>
          </div>
        )}
        <div className="mx-auto max-w-screen-2xl px-4 py-5 sm:px-6 sm:py-6">{children}</div>
      </main>
    </div>
  </div>
);

export default DashboardShell;
